// @ts-nocheck
import { motion, AnimatePresence } from 'framer-motion';
import ShimmerButton from './ShimmerButton';
import { analytics, AnalyticsEvents } from '../utils/analytics';

type ModalVariant =
    | 'new_user'
    | 'returning_no_saved'
    | 'returning_with_saved'
    | 'device_already_paired';

interface UnifiedWelcomeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUseSaved?: () => void;
    onStartFresh: () => void;
    firstName?: string;
    variant: ModalVariant;
    hasDeviceParams?: boolean;
    // New props for device_already_paired variant
    isUpgraded?: boolean;
    onStartTrial?: () => void;
    onDownloadApp?: () => void;
}

export default function UnifiedWelcomeModal({
    isOpen,
    onClose,
    onUseSaved,
    onStartFresh,
    firstName,
    variant,
    hasDeviceParams = false,
    isUpgraded = false,
    onStartTrial,
    onDownloadApp,
}: UnifiedWelcomeModalProps) {
    const handleUseSaved = () => {
        if (onUseSaved) {
            onUseSaved();
        }
        onClose();
    };

    const handleStartFresh = () => {
        onStartFresh();
        onClose();
    };

    const handleDownloadApp = () => {
        // Track app download click
        analytics.capture(AnalyticsEvents.APP_DOWNLOAD_CLICKED, {
            location: 'unified_welcome_modal',
            variant: variant,
        });
        if (onDownloadApp) {
            onDownloadApp();
        }
    };

    const handleStartTrial = () => {
        if (onStartTrial) {
            onStartTrial();
        }
        onClose();
    };

    // Handle the default action when clicking outside the modal
    const handleDefaultAction = () => {
        if (variant === 'returning_with_saved') {
            handleUseSaved();
        } else if (variant === 'device_already_paired') {
            onClose();
        } else {
            handleStartFresh();
        }
    };

    // Get the appropriate title based on variant
    const getTitle = () => {
        switch (variant) {
            case 'new_user':
                return `Welcome${firstName ? `, ${firstName}` : ''}!`;
            case 'returning_no_saved':
            case 'returning_with_saved':
                return `Welcome back${firstName ? `, ${firstName}` : ''}!`;
            case 'device_already_paired':
                return `Welcome back${firstName ? `, ${firstName}` : ''}!`;
            default:
                return 'Welcome!';
        }
    };

    // Get the appropriate subtitle/description based on variant
    const getDescription = () => {
        switch (variant) {
            case 'new_user':
                return "Welcome to Niio! Let's get you set up with amazing digital art for your screen.";
            case 'returning_no_saved':
                return "Great to see you again! Let's get you set up with amazing digital art for your screen.";
            case 'returning_with_saved':
                return 'We found your saved art from last time. You can continue with it or start fresh with new selections.';
            case 'device_already_paired':
                return 'Your device is already connected and displaying art.';
            default:
                return '';
        }
    };

    // Unified step list format with emojis
    const getSteps = () => {
        if (variant === 'device_already_paired') {
            // Match the PairingSuccess component's "What's Next" steps
            return [
                {
                    emoji: '✅',
                    label: 'Art Displaying',
                    description: 'Your art is now displaying on your screen',
                },
                {
                    emoji: '📱',
                    label: 'Get the App',
                    description: 'Download the app to control and customize',
                },
                {
                    emoji: '✨',
                    label: 'Explore',
                    description: 'Explore more content and features',
                },
            ];
        } else if (variant === 'returning_with_saved') {
            return [
                {
                    emoji: '🎨',
                    label: 'Select Artcast',
                    description: 'Continue with saved art or select new',
                },
                {
                    emoji: hasDeviceParams ? '📺' : '📱',
                    label: hasDeviceParams
                        ? 'Automatic Pairing'
                        : 'Pair Your Screen',
                    description: hasDeviceParams
                        ? 'Your screen will connect automatically'
                        : 'Connect using the QR code',
                },
                {
                    emoji: '✨',
                    label: 'Enjoy',
                    description:
                        'Experience museum-quality art on your display',
                },
            ];
        } else {
            return [
                {
                    emoji: '🎨',
                    label: 'Select Artcast',
                    description:
                        'AI will curate selections based on your preferences',
                },
                {
                    emoji: hasDeviceParams ? '📺' : '📱',
                    label: hasDeviceParams
                        ? 'Automatic Pairing'
                        : 'Pair Your Screen',
                    description: hasDeviceParams
                        ? 'Your screen will connect automatically'
                        : 'Connect using the QR code',
                },
                {
                    emoji: '✨',
                    label: 'Enjoy',
                    description:
                        'Experience museum-quality art on your display',
                },
            ];
        }
    };

    // Get the appropriate icon based on variant
    const getIcon = () => {
        if (variant === 'device_already_paired') {
            // Success checkmark icon
            return (
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                        className="w-8 h-8 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                        />
                    </svg>
                </div>
            );
        }
        // Default welcome icon
        return (
            <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                    className="w-8 h-8 text-purple-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                </svg>
            </div>
        );
    };

    // Get the appropriate action buttons based on variant
    const getActionButtons = () => {
        if (variant === 'device_already_paired') {
            return (
                <div className="space-y-3">
                    <ShimmerButton
                        onClick={handleDownloadApp}
                        className="w-full"
                    >
                        <span className="flex items-center justify-center gap-2">
                            Download
                            <img
                                src="/niio_logo_white.png"
                                alt="NIIO"
                                className="h-4 w-auto inline-block"
                                style={{ filter: 'brightness(2)' }}
                            />
                            App
                        </span>
                    </ShimmerButton>

                    {!isUpgraded && (
                        <button
                            onClick={handleStartTrial}
                            className="w-full py-3 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/50 rounded-lg text-white font-medium hover:from-purple-600/30 hover:to-pink-600/30 transition-all duration-200"
                        >
                            Start Your Free Trial
                        </button>
                    )}
                </div>
            );
        } else if (variant === 'returning_with_saved') {
            return (
                <div className="space-y-3">
                    <ShimmerButton onClick={handleUseSaved} className="w-full">
                        Use My Saved Artcast
                    </ShimmerButton>

                    <button
                        onClick={handleStartFresh}
                        className="w-full py-3 text-gray-400 hover:text-white transition-colors duration-200"
                    >
                        Start Fresh with AI Curation
                    </button>
                </div>
            );
        } else {
            return (
                <ShimmerButton onClick={handleStartFresh} className="w-full">
                    Let's Get Started
                </ShimmerButton>
            );
        }
    };

    const steps = getSteps();

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <div
                        className="absolute inset-0 bg-black/80"
                        onClick={handleDefaultAction}
                    />
                    <motion.div
                        className="relative bg-gray-900 rounded-2xl p-8 max-w-md w-full"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ type: 'spring', duration: 0.5 }}
                    >
                        <div className="text-center">
                            {/* Icon */}
                            {getIcon()}

                            {/* Title */}
                            <h3 className="text-2xl font-bold text-white mb-3">
                                {getTitle()}
                            </h3>

                            {/* Description */}
                            <p className="text-gray-400 mb-6">
                                {getDescription()}
                            </p>

                            {/* Steps Section */}
                            <div className="bg-gray-800/50 rounded-lg p-4 mb-6">
                                <p className="text-sm text-gray-300 mb-2 font-semibold">
                                    {variant === 'device_already_paired'
                                        ? "What's Next:"
                                        : variant === 'returning_with_saved'
                                        ? 'Next steps:'
                                        : "Here's what happens next:"}
                                </p>
                                <ol className="text-sm text-gray-400 text-left space-y-2">
                                    {steps.map((step, index) => (
                                        <li
                                            key={index}
                                            className="flex items-start"
                                        >
                                            <span className="mr-2">
                                                {step.emoji}
                                            </span>
                                            <div>
                                                <span className="font-medium text-gray-300">
                                                    {step.label}
                                                </span>
                                                {' - '}
                                                <span className="text-gray-400">
                                                    {step.description}
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            </div>

                            {/* Action Buttons */}
                            {getActionButtons()}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
